import type { ChangeEvent, FC, ReactNode } from 'react';

import Avatar from '@/common-components/Avatar';
import Pill from '@/common-components/Pill';
import ReportsList from '@/common-components/ReportsList';
import { cn } from '@/utils/cn.util';
import { formatDate, timeAgo } from '@/utils/date.util';
import type {
  AdminReportListItem,
  AdminShouttDetail,
  AdminShouttListItem,
  AiFlaggedItem,
  ModerationNoteForm,
  ModerationSelection,
} from '@/types/moderation.types';

import { REPORT_TARGET_PILL, SHOUTT_TYPE_PILL } from '../constants';
import ModerationStatusPill from './ModerationStatusPill';

interface ModerationDetailDrawerProps {
  selected: ModerationSelection | null;
  shouttDetail: AdminShouttDetail | undefined;
  isLoadingDetail: boolean;
  isErrorDetail: boolean;
  mutatingId: string | null;
  noteFormFor: ModerationNoteForm | null;
  noteDraft: string;
  noteError: string | null;
  onClose: () => void;
  onApprove: (item: AdminShouttListItem) => void;
  onOpenReject: (item: AdminShouttListItem) => void;
  onRemove: (item: AdminShouttListItem) => void;
  onRestore: (item: AdminShouttListItem) => void;
  onOpenResolve: (item: AdminReportListItem) => void;
  onDismissAiFlag: (item: AiFlaggedItem) => void;
  onRemoveAiFlagged: (item: AiFlaggedItem) => void;
  onChangeNote: (value: string) => void;
  onCancelNote: () => void;
  onSubmitNote: () => void;
}

const InfoRow: FC<{ label: string; value: ReactNode }> = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 border-b border-[var(--color-divider)] py-2.5 last:border-b-0">
    <span className="shrink-0 text-[12.5px] font-medium text-[var(--color-ink-muted)]">
      {label}
    </span>
    <span className="text-right text-[13px] text-[var(--color-ink)]">{value}</span>
  </div>
);

const SectionHeading: FC<{ children: ReactNode }> = ({ children }) => (
  <p className="mb-1 text-[10.5px] font-bold uppercase tracking-wider text-[var(--color-ink-soft)]">
    {children}
  </p>
);

const footerBtn =
  'w-full cursor-pointer rounded-lg py-2.5 text-[13px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50';

const ModerationDetailDrawer: FC<ModerationDetailDrawerProps> = ({
  selected,
  shouttDetail,
  isLoadingDetail,
  isErrorDetail,
  mutatingId,
  noteFormFor,
  noteDraft,
  noteError,
  onClose,
  onApprove,
  onOpenReject,
  onRemove,
  onRestore,
  onOpenResolve,
  onDismissAiFlag,
  onRemoveAiFlagged,
  onChangeNote,
  onCancelNote,
  onSubmitNote,
}) => {
  // A flagged listing has its own drawer; this one covers shoutts and reports.
  const isOpen = selected !== null && selected.kind !== 'aiListing';
  const isMutating = !!(selected && mutatingId === selected.item.id);

  const handleNote = (e: ChangeEvent<HTMLTextAreaElement>): void => onChangeNote(e.target.value);

  const noteForm = (
    <div className="flex flex-col gap-2">
      <textarea
        value={noteDraft}
        onChange={handleNote}
        rows={3}
        placeholder={
          noteFormFor === 'reject'
            ? 'Why is this Shoutt being rejected? (shown internally)'
            : 'Optional resolution note (e.g. “warned the user”)'
        }
        className="w-full resize-none rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-muted)] px-3 py-2 text-[12.5px] text-[var(--color-ink)] placeholder:text-[var(--color-ink-soft)] focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
      />
      {noteError ? (
        <p className="text-[11.5px] font-medium text-[var(--color-danger-ink)]">{noteError}</p>
      ) : null}
      <div className="flex gap-2">
        <button
          type="button"
          disabled={isMutating}
          onClick={onCancelNote}
          className={cn(
            footerBtn,
            'border border-[var(--color-divider)] bg-white text-[var(--color-ink-muted)] hover:bg-[var(--color-surface-muted)]',
          )}
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={isMutating}
          onClick={onSubmitNote}
          className={cn(
            footerBtn,
            noteFormFor === 'reject'
              ? 'border border-[var(--color-danger)] bg-[var(--color-danger-bg)] text-[var(--color-danger-ink)] hover:bg-[var(--color-danger)] hover:text-white'
              : 'border border-[var(--color-success)] bg-[var(--color-success-bg)] text-[var(--color-success-ink)] hover:bg-[var(--color-success)] hover:text-white',
          )}
        >
          {isMutating
            ? 'Working…'
            : noteFormFor === 'reject'
              ? 'Confirm Reject'
              : 'Confirm Resolve'}
        </button>
      </div>
    </div>
  );

  // An AI Flagged row carries only an id and a verdict, so that variant renders from the detail
  // fetch — until it lands there is nothing to show but a spinner.
  const aiItem = selected?.kind === 'aiShoutt' ? selected.item : null;
  const shouttItem =
    selected?.kind === 'shoutt' ? selected.item : aiItem ? (shouttDetail ?? null) : null;
  const reportItem = selected?.kind === 'report' ? selected.item : null;
  const isAiMutating = !!(aiItem && mutatingId === aiItem.id);
  const shoutt = shouttDetail ?? undefined;
  const shouttPulled = shouttItem
    ? (shoutt?.status ?? shouttItem.status) === 'removed' ||
      (shoutt?.moderation.state ?? shouttItem.moderation.state) === 'rejected'
    : false;

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/40 transition-opacity duration-300',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={cn(
          'fixed bottom-0 right-0 top-0 z-50 flex w-full max-w-[480px] flex-col bg-white shadow-2xl',
          'overflow-hidden transition-transform duration-300 ease-in-out',
          isOpen ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        {/* Header bar */}
        <div className="flex items-center justify-between border-b border-[var(--color-divider)] px-5 py-4">
          <h2 className="text-[15px] font-bold text-[var(--color-ink)]">
            {selected?.kind === 'report'
              ? 'Report Details'
              : selected?.kind === 'aiShoutt'
                ? 'Flagged Shoutt'
                : 'Shoutt Details'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-[var(--color-ink-muted)] transition-colors hover:bg-[var(--color-surface-muted-2)]"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
              <path d="M3.22 3.22a.75.75 0 0 1 1.06 0L8 6.94l3.72-3.72a.75.75 0 1 1 1.06 1.06L9.06 8l3.72 3.72a.75.75 0 1 1-1.06 1.06L8 9.06l-3.72 3.72a.75.75 0 0 1-1.06-1.06L6.94 8 3.22 4.28a.75.75 0 0 1 0-1.06z" />
            </svg>
          </button>
        </div>

        {/* ── Shoutt variant ── */}
        {aiItem && !shouttItem ? (
          <div className="flex flex-1 items-center justify-center text-[13px] text-[var(--color-ink-muted)]">
            {isErrorDetail ? 'Failed to load this Shoutt.' : 'Loading details…'}
          </div>
        ) : null}

        {shouttItem ? (
          <>
            <div className="border-b border-[var(--color-divider)] bg-[var(--color-surface-muted)] px-5 py-4">
              <div className="flex items-center gap-3">
                <Avatar name={shouttItem.author.name} src={shouttItem.author.avatarUrl} size="lg" />
                <div className="min-w-0">
                  <div className="truncate text-[15px] font-bold text-[var(--color-ink)]">
                    {shouttItem.author.name}
                  </div>
                  <div className="mt-0.5 text-[12px] text-[var(--color-ink-soft)]">
                    Trust {shouttItem.author.trustScore} · {shouttItem.campusName}
                  </div>
                </div>
                <div className="ml-auto">
                  <ModerationStatusPill
                    kind="shouttStatus"
                    value={shoutt?.status ?? shouttItem.status}
                  />
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {isLoadingDetail ? (
                <div className="flex items-center justify-center py-16 text-[13px] text-[var(--color-ink-muted)]">
                  Loading details…
                </div>
              ) : isErrorDetail ? (
                <div className="flex items-center justify-center py-16 text-[13px] text-[var(--color-danger-ink)]">
                  Failed to load details. Try closing and reopening.
                </div>
              ) : shoutt ? (
                <>
                  {aiItem ? (
                    <div className="border-b border-[var(--color-divider)] bg-[var(--color-pink-bg)] px-5 py-3">
                      <p className="text-[10.5px] font-bold uppercase tracking-wider text-[var(--color-pink-ink)]">
                        🤖 AI flag
                      </p>
                      <p className="mt-1 text-[12.5px] leading-relaxed text-[var(--color-ink)]">
                        {aiItem.flagReason}
                      </p>
                    </div>
                  ) : null}

                  <div className="px-5 py-4">
                    <SectionHeading>Post</SectionHeading>
                    <p className="py-1 text-[13px] leading-relaxed text-[var(--color-ink-muted)]">
                      “{shoutt.body}”
                    </p>
                  </div>

                  <div className="mx-5 border-t border-[var(--color-divider)]" />

                  <div className="px-5 py-4">
                    <SectionHeading>Moderation</SectionHeading>
                    <InfoRow
                      label="Type"
                      value={
                        <Pill tone={SHOUTT_TYPE_PILL[shoutt.type].tone}>
                          {SHOUTT_TYPE_PILL[shoutt.type].label}
                        </Pill>
                      }
                    />
                    <InfoRow
                      label="Moderation state"
                      value={
                        <ModerationStatusPill kind="moderationState" value={shoutt.moderation.state} />
                      }
                    />
                    <InfoRow
                      label="AI flagged"
                      value={
                        shoutt.moderation.aiFlagged ? (
                          <span className="font-semibold text-[var(--color-danger-ink)]">
                            🤖 Yes
                          </span>
                        ) : (
                          'No'
                        )
                      }
                    />
                    {shoutt.moderation.flagReason ? (
                      <InfoRow label="Flag reason" value={shoutt.moderation.flagReason} />
                    ) : null}
                    <InfoRow label="Responses" value={shoutt.responseCount} />
                    {shoutt.teamSize ? <InfoRow label="Team size" value={shoutt.teamSize} /> : null}
                  </div>

                  <div className="mx-5 border-t border-[var(--color-divider)]" />

                  <div className="px-5 py-4">
                    <SectionHeading>
                      Reports
                      {shoutt.moderation.reportCount > 0
                        ? ` (${shoutt.moderation.reportCount})`
                        : ''}
                    </SectionHeading>
                    <ReportsList
                      reports={shoutt.reports}
                      totalCount={shoutt.moderation.reportCount}
                    />
                  </div>

                  {shoutt.recentResponses.length > 0 ? (
                    <>
                      <div className="mx-5 border-t border-[var(--color-divider)]" />
                      <div className="px-5 py-4">
                        <SectionHeading>Recent Responses</SectionHeading>
                        <div className="flex flex-col gap-2.5 pt-1">
                          {shoutt.recentResponses.map((response) => (
                            <div
                              key={response.id}
                              className="rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-muted)] p-3"
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-[12px] font-semibold text-[var(--color-ink)]">
                                  {response.responderName}
                                </span>
                                <span className="text-[11px] text-[var(--color-ink-soft)]">
                                  {timeAgo(response.createdAt)}
                                </span>
                              </div>
                              <p className="mt-1 text-[12px] leading-relaxed text-[var(--color-ink-muted)]">
                                “{response.message}”
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : null}

                  <div className="mx-5 border-t border-[var(--color-divider)]" />

                  <div className="px-5 py-4">
                    <SectionHeading>Shoutt</SectionHeading>
                    <InfoRow label="Posted" value={formatDate(shoutt.createdAt)} />
                    <InfoRow label="Last Updated" value={formatDate(shoutt.updatedAt)} />
                  </div>
                </>
              ) : null}
            </div>

            {/* Footer actions */}
            <div className="border-t border-[var(--color-divider)] bg-white px-5 py-4">
              {aiItem ? (
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={isAiMutating}
                    onClick={() => onDismissAiFlag(aiItem)}
                    className={cn(
                      footerBtn,
                      'border border-[var(--color-divider)] bg-white text-[var(--color-ink-muted)] hover:bg-[var(--color-surface-muted)]',
                    )}
                  >
                    {isAiMutating ? 'Working…' : 'Dismiss Flag'}
                  </button>
                  <button
                    type="button"
                    disabled={isAiMutating}
                    onClick={() => onRemoveAiFlagged(aiItem)}
                    className={cn(
                      footerBtn,
                      'border border-[var(--color-danger)] bg-[var(--color-danger-bg)] text-[var(--color-danger-ink)] hover:bg-[var(--color-danger)] hover:text-white',
                    )}
                  >
                    {isAiMutating ? 'Working…' : 'Remove Shoutt'}
                  </button>
                </div>
              ) : noteFormFor === 'reject' ? (
                noteForm
              ) : shouttPulled ? (
                <button
                  type="button"
                  disabled={isMutating}
                  onClick={() => onRestore(shouttItem)}
                  className={cn(
                    footerBtn,
                    'border border-[var(--color-success)] bg-[var(--color-success-bg)] text-[var(--color-success-ink)] hover:bg-[var(--color-success)] hover:text-white',
                  )}
                >
                  {isMutating ? 'Restoring…' : 'Restore Shoutt'}
                </button>
              ) : (
                <div className="flex gap-2">
                  {(shoutt?.moderation.state ?? shouttItem.moderation.state) === 'pending' ? (
                    <button
                      type="button"
                      disabled={isMutating}
                      onClick={() => onApprove(shouttItem)}
                      className={cn(
                        footerBtn,
                        'border border-[var(--color-success)] bg-[var(--color-success-bg)] text-[var(--color-success-ink)] hover:bg-[var(--color-success)] hover:text-white',
                      )}
                    >
                      {isMutating ? 'Working…' : '✓ Approve'}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    disabled={isMutating}
                    onClick={() => onOpenReject(shouttItem)}
                    className={cn(
                      footerBtn,
                      'border border-[var(--color-danger)] bg-[var(--color-danger-bg)] text-[var(--color-danger-ink)] hover:bg-[var(--color-danger)] hover:text-white',
                    )}
                  >
                    ✗ Reject
                  </button>
                  <button
                    type="button"
                    disabled={isMutating}
                    onClick={() => onRemove(shouttItem)}
                    className={cn(
                      footerBtn,
                      'border border-[var(--color-divider)] bg-[var(--color-surface-muted)] text-[var(--color-ink-muted)] hover:bg-[var(--color-surface-muted-2)]',
                    )}
                  >
                    {isMutating ? 'Working…' : '🗑 Remove'}
                  </button>
                </div>
              )}
            </div>
          </>
        ) : null}

        {/* ── Report variant ── */}
        {reportItem ? (
          <>
            <div className="border-b border-[var(--color-divider)] bg-[var(--color-surface-muted)] px-5 py-4">
              <div className="flex items-center gap-3">
                <Avatar name={reportItem.reporter.name} src={reportItem.reporter.avatarUrl} size="lg" />
                <div className="min-w-0">
                  <div className="truncate text-[15px] font-bold text-[var(--color-ink)]">
                    {reportItem.reporter.name}
                  </div>
                  <div className="mt-0.5 text-[12px] text-[var(--color-ink-soft)]">
                    Filed {timeAgo(reportItem.createdAt)}
                  </div>
                </div>
                <div className="ml-auto">
                  <ModerationStatusPill kind="reportStatus" value={reportItem.status} />
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="px-5 py-4">
                <SectionHeading>Reason</SectionHeading>
                <p className="py-1 text-[13px] leading-relaxed text-[var(--color-ink-muted)]">
                  “{reportItem.reason}”
                </p>
              </div>

              <div className="mx-5 border-t border-[var(--color-divider)]" />

              <div className="px-5 py-4">
                <SectionHeading>Target</SectionHeading>
                <InfoRow
                  label="Type"
                  value={
                    <Pill tone={REPORT_TARGET_PILL[reportItem.targetType].tone}>
                      {REPORT_TARGET_PILL[reportItem.targetType].label}
                    </Pill>
                  }
                />
                <InfoRow label="Target" value={reportItem.target.label} />
                {reportItem.target.sublabel ? (
                  <InfoRow label="Contact" value={reportItem.target.sublabel} />
                ) : null}
                {reportItem.target.status ? (
                  <InfoRow label="Target status" value={reportItem.target.status} />
                ) : null}
              </div>

              <div className="mx-5 border-t border-[var(--color-divider)]" />

              <div className="px-5 py-4">
                <SectionHeading>Report</SectionHeading>
                <InfoRow label="Filed" value={formatDate(reportItem.createdAt)} />
                {reportItem.resolutionNote ? (
                  <InfoRow label="Resolution note" value={reportItem.resolutionNote} />
                ) : null}
              </div>
            </div>

            {reportItem.status !== 'resolved' ? (
              <div className="border-t border-[var(--color-divider)] bg-white px-5 py-4">
                {noteFormFor === 'resolve' ? (
                  noteForm
                ) : (
                  <button
                    type="button"
                    disabled={isMutating}
                    onClick={() => onOpenResolve(reportItem)}
                    className={cn(
                      footerBtn,
                      'border border-[var(--color-success)] bg-[var(--color-success-bg)] text-[var(--color-success-ink)] hover:bg-[var(--color-success)] hover:text-white',
                    )}
                  >
                    {isMutating ? 'Resolving…' : '✓ Resolve Report'}
                  </button>
                )}
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </>
  );
};

export default ModerationDetailDrawer;
